import React from "react";
import { useColors } from "../../constants/Colors";
import { useTranslation } from "react-i18next";

interface ChatMessage {
  message_id: number;
  conversation_id: number;
  sender_id: number;
  message: string | null;
  type: string;
  created_at: string | null;
  url?: string;
  filename?: string;
}

type StepState = "fulfilled" | "current" | "unfulfilled";

interface HowToFlaterooCardProps {
  messages: ChatMessage[];
  state: string;
}

const HowToFlaterooCard: React.FC<HowToFlaterooCardProps> = ({
  messages,
  state,
}) => {
  const colors = useColors();
  const { t } = useTranslation();

  const hasNonApplicationMessage = messages.some(
    (item) => item.type !== "application",
  );

  const steps: { title: string; subtitle: string; state: StepState }[] = [
    {
      title: t("how_to_flateroo.steps.apply.title") || "Apply",
      subtitle:
        t("how_to_flateroo.steps.apply.subtitle", { method: "text" }) ||
        "You applied via text",
      state: "fulfilled",
    },
    {
      title: t("how_to_flateroo.steps.chat.title") || "Response",
      subtitle:
        t("how_to_flateroo.steps.chat.subtitle") ||
        "The shared flat has to write the first message",
      state:
        hasNonApplicationMessage || state === "accepted"
          ? "fulfilled"
          : "current",
    },
    {
      title: t("how_to_flateroo.steps.meet.title") || "Chat & Meet",
      subtitle:
        t("how_to_flateroo.steps.meet.subtitle") ||
        "Chat with your future flatmates and meet up",
      state:
        state === "accepted"
          ? "fulfilled"
          : hasNonApplicationMessage
            ? "current"
            : "unfulfilled",
    },
  ];

  const getLineColor = (prevState: StepState) => {
    return prevState === "fulfilled"
      ? colors.accentColor
      : colors.textNeutralMuted + "66";
  };

  return (
    <div className="flex justify-center py-8">
      <div
        className="w-[80%] max-w-md rounded-3xl p-6"
        style={{ backgroundColor: colors.containerNeutralLightLowest }}
      >
        <p
          className="text-lg font-semibold"
          style={{ color: colors.textNeutralDefault }}
        >
          {t("how_to_flateroo.title_top") || "How to"}
        </p>
        <img
          src="/240822-Flateroo-Logo-black-rgb.svg"
          alt="Flateroo"
          className="h-10 w-auto"
          style={{
            filter:
              colors.textNeutralDefault === "#FFFFFF" ? "invert(1)" : "none",
          }}
        />

        <div className="flex flex-col my-4">
          {steps.map((step, index) => (
            <div
              key={index}
              className="flex items-center relative w-full"
              style={{ height: 90 }}
            >
              {/* Vertical line connecting to previous step */}
              {index > 0 && (
                <div
                  className="absolute"
                  style={{
                    width: 4,
                    height: 45,
                    left: 22,
                    top: -20,
                    backgroundColor: getLineColor(steps[index - 1].state),
                    borderRadius: 2,
                  }}
                />
              )}

              {/* Step indicator */}
              <div
                className="flex items-center justify-center flex-shrink-0"
                style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  borderWidth: step.state === "current" ? 4 : 0,
                  borderStyle: "solid",
                  borderColor:
                    step.state === "current"
                      ? colors.accentColor + "44"
                      : "transparent",
                }}
              >
                <div
                  className="flex items-center justify-center"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: "50%",
                    backgroundColor:
                      step.state === "fulfilled"
                        ? colors.accentColor
                        : "transparent",
                    border:
                      step.state !== "fulfilled"
                        ? `1px solid ${
                            step.state === "current"
                              ? colors.accentColor
                              : colors.textNeutralMuted + "66"
                          }`
                        : "none",
                  }}
                >
                  <div
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor:
                        step.state === "unfulfilled"
                          ? colors.textNeutralMuted + "66"
                          : step.state === "current"
                            ? colors.accentColor
                            : "rgba(255, 255, 255, 0.85)",
                    }}
                  />
                </div>
              </div>

              {/* Step text */}
              <div className="flex-1 pl-4">
                <p
                  className="font-semibold"
                  style={{ color: colors.textNeutralDefault }}
                >
                  {step.title}
                </p>
                <p
                  className="text-sm leading-tight"
                  style={{ color: colors.textNeutralMuted }}
                >
                  {step.subtitle}
                </p>
              </div>
            </div>
          ))}
        </div>

        <p
          className="text-center text-xs"
          style={{ color: colors.textNeutralMuted }}
        >
          {t("by_students_for_students") || "By students, for students"}
        </p>
      </div>
    </div>
  );
};

export default HowToFlaterooCard;
